<template>
  <section
    class="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#f4f4f4] via-[#f8f9fa] to-[#e9ecef]"
  >
    <!-- Floating Background Shapes -->
    <!-- <div class="floating-shape floating-shape-1"></div>
    <div class="floating-shape floating-shape-2"></div>
    <div class="floating-shape floating-shape-3"></div>
    <div class="floating-shape floating-shape-4"></div> -->

    <!-- Swiper Container -->
    <div class="swiper hero-swiper">
      <div class="swiper-wrapper">
        <!-- Slide Content -->
        <div
          v-for="(slide, index) in slides"
          :key="index"
          class="swiper-slide relative z-10 flex flex-col items-center text-center p-4 md:p-8"
        >
          <h1
            class="hero-title font-black leading-tight tracking-tighter gradient-text mb-4 md:mb-6 fade-in-up"
            :class="{ 'aos-animate': currentSlide === index }"
            :style="{ animationDelay: '0.2s' }"
          >
            {{ $t(slide.titleKey) }}
          </h1>

          <p
            class="hero-subtitle font-normal text-[#0b105d] opacity-80 max-w-xl md:max-w-2xl mb-8 md:mb-10 fade-in-up"
            :class="{ 'aos-animate': currentSlide === index }"
            :style="{ animationDelay: '0.4s' }"
          >
            {{ $t(slide.descriptionKey) }}
          </p>

          <div
            class="flex flex-col sm:flex-row gap-4 md:gap-6 fade-in-up"
            :class="{ 'aos-animate': currentSlide === index }"
            :style="{ animationDelay: '0.6s' }"
          >
            <router-link
              :to="slide.primaryAction.route"
              class="cta-button relative overflow-hidden flex items-center justify-center rounded-full py-3 px-8 md:py-4 md:px-10 text-white text-base md:text-lg font-bold shadow-lg hover:scale-105 transform transition-transform duration-300"
              :class="slide.primaryAction.bgClass"
            >
              <span class="relative z-10">{{ $t(slide.primaryAction.textKey) }}</span>
              <div class="rotating-glow" :style="slide.primaryAction.glowStyle"></div>
            </router-link>

            <router-link
              v-if="slide.secondaryAction"
              :to="slide.secondaryAction.route"
              class="cta-button relative overflow-hidden flex items-center justify-center rounded-full py-3 px-8 md:py-4 md:px-10 bg-transparent text-base md:text-lg font-bold shadow-lg hover:scale-105 transform transition-all duration-300"
              :class="slide.secondaryAction.classes"
            >
              <span class="relative z-10">{{ $t(slide.secondaryAction.textKey) }}</span>
              <div class="rotating-glow" :style="slide.secondaryAction.glowStyle"></div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Swiper Pagination -->
      <div class="swiper-pagination"></div>
    </div>

    <!-- Auto-cycle Progress (Optional) -->
    <div
      v-if="autoPlay"
      class="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 w-32 h-1 bg-white/20 rounded-full overflow-hidden"
    >
      <div
        class="h-full bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] transition-all ease-linear"
        :style="{ width: `${progress}%` }"
      ></div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Swiper } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { onMounted, onUnmounted, ref } from 'vue'

const currentSlide = ref(0)
const progress = ref(0)
const autoPlay = ref(true)
const autoplayDelay = 5000 // ms
let swiper: Swiper | null = null

const slides = [
  {
    titleKey: 'hero.title',
    descriptionKey: 'hero.subtitle',
    primaryAction: {
      textKey: 'hero.ctaButton',
      route: '/about',
      bgClass: 'bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c]',
      glowStyle: 'background: conic-gradient(from 0deg, #fe6d0d, #ffb47c, #fe6d0d);',
    },
    secondaryAction: {
      textKey: 'common.learnMore',
      route: '/about',
      classes: 'border-2 border-[#fe6d0d] text-[#fe6d0d] hover:bg-[#fe6d0d] hover:text-white',
      glowStyle: 'background: conic-gradient(from 0deg, #ffb47c, #0b105d, #ffb47c);',
    },
  },
  {
    titleKey: 'hero.slide1_title',
    descriptionKey: 'hero.slide1_desc',
    primaryAction: {
      textKey: 'nav.getStarted',
      route: '/contact',
      bgClass: 'bg-gradient-to-r from-[#ffb47c] to-[#fe6d0d]',
      glowStyle: 'background: conic-gradient(from 0deg, #ffb47c, #fe6d0d, #ffb47c);',
    },
  },
  {
    titleKey: 'hero.slide2_title',
    descriptionKey: 'hero.slide2_desc',
    primaryAction: {
      textKey: 'common.joinCommunity',
      route: '/contact',
      bgClass: 'bg-gradient-to-r from-[#0b105d] to-[#fe6d0d]',
      glowStyle: 'background: conic-gradient(from 0deg, #0b105d, #fe6d0d, #0b105d);',
    },
  },
]

// Initialize Swiper
const initSwiper = () => {
  swiper = new Swiper('.hero-swiper', {
    modules: [Navigation, Pagination, Autoplay],
    loop: true,
    autoplay: {
      delay: autoplayDelay,
      disableOnInteraction: false,
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    speed: 300,
    effect: 'slide',
    on: {
      autoplayTimeLeft: (s, time, progressPercentage) => {
        progress.value = (1 - progressPercentage) * 100
      },
      slideChange: (s) => {
        currentSlide.value = s.realIndex
        progress.value = 0
      },
    },
  })
}

// Update setCurrentSlide to work with Swiper
const setCurrentSlide = (index: number) => {
  if (swiper) {
    swiper.slideToLoop(index)
  }
}

// Lifecycle
onMounted(() => {
  initSwiper()

  // Initial animation trigger
  setTimeout(() => {
    const firstSlideElements = document.querySelectorAll('.swiper-slide:first-child .fade-in-up')
    firstSlideElements.forEach((el) => {
      el.classList.add('aos-animate')
    })
  }, 100)
})

onUnmounted(() => {
  if (swiper) {
    swiper.destroy(true, true)
  }
})
</script>

<style scoped>
:root {
  --brand-primary: #fe6d0d;
  --brand-secondary: #ffb47c;
  --brand-accent: #0b105d;
  --gradient-start: #f4f4f4;
  --gradient-mid: #f8f9fa;
  --gradient-end: #e9ecef;
}

.gradient-text {
  background-image: linear-gradient(
    to right,
    var(--brand-primary),
    var(--brand-secondary),
    var(--brand-accent)
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-title {
  font-size: clamp(2.25rem, 5vw + 1rem, 6rem);
}

.hero-subtitle {
  font-size: clamp(1rem, 2vw + 0.5rem, 1.5rem);
}

.glass-button {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.glass-button:hover {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 15px rgba(254, 109, 13, 0.5);
}

.swiper-pagination {
  position: absolute !important;
  bottom: 2.5rem !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  z-index: 10 !important;
}

.swiper-pagination-bullet {
  width: 12px !important;
  height: 12px !important;
  border-radius: 50% !important;
  background-color: rgba(11, 16, 93, 0.3) !important;
  border: 2px solid var(--brand-accent) !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
  cursor: pointer !important;
  opacity: 1 !important;
}

.swiper-pagination-bullet-active {
  background-color: var(--brand-primary) !important;
  transform: scale(1.2) !important;
}

.floating-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.05;
  animation: float 15s ease-in-out infinite alternate;
}

.floating-shape-1 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, var(--brand-primary), transparent 70%);
  top: 10%;
  left: 5%;
  animation-duration: 18s;
}

.floating-shape-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, var(--brand-secondary), transparent 70%);
  bottom: 15%;
  right: 10%;
  animation-duration: 16s;
}

.floating-shape-3 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, var(--brand-accent), transparent 70%);
  top: 20%;
  right: 20%;
  animation-duration: 17s;
}

.floating-shape-4 {
  width: 250px;
  height: 250px;
  background: radial-gradient(
    circle,
    var(--brand-primary),
    var(--brand-secondary),
    transparent 80%
  );
  bottom: 5%;
  left: 15%;
  animation-duration: 15s;
  opacity: 0.08;
}

@keyframes float {
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  100% {
    transform: translateY(-30px) rotate(45deg);
  }
}

.rotating-glow {
  position: absolute;
  inset: -5px;
  border-radius: 9999px;
  background: conic-gradient(
    from 0deg,
    var(--brand-primary),
    var(--brand-secondary),
    var(--brand-primary)
  );
  filter: blur(15px);
  opacity: 0;
  animation: rotateGlow 5s linear infinite paused;
  z-index: -1;
}

.cta-button:hover .rotating-glow {
  animation-play-state: running;
  opacity: 0.7;
}

@keyframes rotateGlow {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.hero-swiper {
  width: 100%;
  height: 100vh;
}

.swiper-slide {
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-in-up {
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.8s ease-out,
    transform 0.8s ease-out;
}

.fade-in-up.aos-animate {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 640px) {
  .floating-shape-1 {
    width: 120px;
    height: 120px;
  }
  .floating-shape-2 {
    width: 180px;
    height: 180px;
  }
  .floating-shape-3 {
    width: 100px;
    height: 100px;
  }
  .floating-shape-4 {
    width: 150px;
    height: 150px;
  }
}
</style>
